import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpen, Users } from 'lucide-react';
import LaborOperationsLayout from '../../../components/labor/LaborOperationsLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { getAdminLaborCases } from '../../../features/labor-intake/laborIntakeAdminService';

const formatDate = (value) =>
  value ? new Intl.DateTimeFormat('ar-SA', { dateStyle: 'medium' }).format(new Date(value)) : '-';

const clientName = (client = {}) =>
  client.fullName || [client.firstName, client.fatherName, client.grandfatherName, client.familyName].filter(Boolean).join(' ') || 'عميل غير مسمى';

const clientKey = (item) =>
  item.client?.nationalId || item.client?.mobile || item.client?.email || clientName(item.client);

const LaborClientsPage = () => {
  const { user, token } = useAdminAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminLaborCases(user, token)
      .then(setCases)
      .finally(() => setLoading(false));
  }, [token, user]);

  const clients = useMemo(() => {
    const grouped = new Map();
    cases.forEach((item) => {
      const key = clientKey(item);
      const current = grouped.get(key) || {
        key,
        name: clientName(item.client),
        mobile: item.client?.mobile || '-',
        email: item.client?.email || '-',
        requests: [],
      };
      current.requests.push(item);
      grouped.set(key, current);
    });
    return Array.from(grouped.values()).sort((a, b) => b.requests.length - a.requests.length);
  }, [cases]);

  return (
    <LaborOperationsLayout>
      <main className="px-6 pb-12 pt-8 sm:px-10" dir="rtl">
        <header className="mb-8">
          <p className="lo-kicker font-bold">CLIENTS</p>
          <h1 className="legal-title mt-3 text-[34px] font-bold text-[#293a50]">العملاء</h1>
          <p className="mt-3 text-[15px] leading-7 text-[#697483]">قائمة أولية تربط كل عميل بالطلبات المسجلة داخل بوابة القضايا العمالية.</p>
        </header>

        <section className="lo-surface overflow-hidden rounded-[18px]">
          <div className="flex items-center justify-between border-b border-[#e7e2d9] px-6 py-5">
            <h2 className="text-[18px] font-bold text-[#293a50]">سجل العملاء</h2>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#f1eee8] px-4 py-2 text-sm font-bold text-[#806f58]">
              <Users className="h-4 w-4" /> {clients.length} عميل
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-right text-[15px]">
              <thead className="bg-[#f4f1eb] text-[13px] font-bold text-[#806f58]">
                <tr>
                  <th className="px-6 py-4">العميل</th>
                  <th className="px-6 py-4">الجوال</th>
                  <th className="px-6 py-4">البريد</th>
                  <th className="px-6 py-4">عدد الطلبات</th>
                  <th className="px-6 py-4">آخر طلب</th>
                  <th className="px-6 py-4">الطلبات المرتبطة</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => {
                  const latest = client.requests[0];
                  return (
                    <tr key={client.key} className="border-b border-[#ece8e0] transition hover:bg-[#faf8f3]">
                      <td className="px-6 py-5 font-bold text-[#293a50]">{client.name}</td>
                      <td className="px-6 py-5 text-[#697483]">{client.mobile}</td>
                      <td className="px-6 py-5 text-[#697483]">{client.email}</td>
                      <td className="px-6 py-5 font-bold text-[#293a50]">{client.requests.length}</td>
                      <td className="px-6 py-5 text-[#697483]">{formatDate(latest?.submittedAt)}</td>
                      <td className="px-6 py-5">
                        <div className="flex flex-wrap gap-2">
                          {client.requests.slice(0, 4).map((request) => (
                            <Link key={request.id} to={`/admin/labor-intake/${request.id}`} className="inline-flex items-center gap-2 rounded-full bg-[#f1eee8] px-3 py-1.5 text-xs font-bold text-[#293a50] hover:bg-[#e8e4dc]">
                              <FolderOpen className="h-3.5 w-3.5 text-[#948066]" />
                              {request.referenceNumber}
                            </Link>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!loading && !clients.length ? (
                  <tr><td colSpan="6" className="px-6 py-10 text-center text-sm text-[#697483]">لا توجد طلبات مسجلة بعد.</td></tr>
                ) : null}
                {loading ? (
                  <tr><td colSpan="6" className="px-6 py-10 text-center text-sm text-[#697483]">جارٍ تحميل العملاء...</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </LaborOperationsLayout>
  );
};

export default LaborClientsPage;
